import { useState, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ImagePlus, Upload, X, ClipboardPaste } from 'lucide-react';
import { toast } from 'sonner';
import { submitChangeRequest } from '@/lib/submission';

interface SuggestImageDialogProps {
  variantId: string;
  variantLabel: string; // f.eks. "Tesla Model Y Long Range"
  brandSlug: string;
  modelSlug: string;
  variantSlug: string;
  triggerVariant?: 'outline' | 'default' | 'ghost';
  triggerSize?: 'sm' | 'default';
}

export function SuggestImageDialog({
  variantId,
  variantLabel,
  brandSlug,
  modelSlug,
  variantSlug,
  triggerVariant = 'outline',
  triggerSize = 'sm',
}: SuggestImageDialogProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [pendingImagePath, setPendingImagePath] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sourceUrl, setSourceUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setImageUrl('');
    setPendingImagePath(null);
    setSourceUrl('');
    setNotes('');
    setSubmitterEmail('');
    setSubmitterName('');
    setHoneypot('');
  };

  const uploadBlob = async (blob: Blob, extension: string) => {
    if (!user) {
      toast.error('Log ind for at uploade — eller indsæt en billede-URL i stedet');
      return;
    }
    setUploading(true);
    try {
      const ts = Date.now();
      const path = `pending/${user.id}/${brandSlug}-${modelSlug}-${variantSlug}-${ts}.${extension}`;
      const { error: uploadError } = await supabase.storage
        .from('car-images')
        .upload(path, blob, {
          upsert: false,
          contentType: `image/${extension}`,
        });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage
        .from('car-images')
        .getPublicUrl(path);
      if (urlData?.publicUrl) {
        setPendingImagePath(path);
        setImageUrl(urlData.publicUrl);
        toast.success('Billede uploadet');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Kunne ikke uploade billedet. Prøv igen eller indsæt en URL i stedet.');
    } finally {
      setUploading(false);
    }
  };

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Filen er ikke et billede');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Billedet må højst fylde 10 MB');
      return;
    }
    const ext = file.type.split('/')[1] || 'png';
    await uploadBlob(file, ext);
  };

  const handlePaste = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find((t) => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          await uploadBlob(blob, imageType.split('/')[1] || 'png');
          return;
        }
      }
      toast.error('Intet billede i udklipsholder');
    } catch (err) {
      console.error('Paste error:', err);
      toast.error('Kunne ikke læse udklipsholder');
    }
  };

  const handleSubmit = async () => {
    if (!imageUrl.trim()) {
      toast.error('Tilføj et billede eller en billede-URL');
      return;
    }
    if (!user && !submitterEmail.trim()) {
      toast.error('E-mail er påkrævet');
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitChangeRequest('suggest_image', {
        entity_type: 'variant',
        entity_id: variantId,
        proposed_data: pendingImagePath
          ? { pending_image_path: pendingImagePath }
          : { image_url: imageUrl.trim() },
        notes: notes.trim() || null,
        source_url: sourceUrl.trim() || null,
        ...(!user && {
          submitter_email: submitterEmail.trim(),
          submitter_name: submitterName.trim() || undefined,
        }),
        website: honeypot || undefined,
      });
      if (!result.ok) throw new Error(result.userMessage);
      toast.success('Tak! Dit billed-forslag er sendt til godkendelse.');
      reset();
      setOpen(false);
    } catch (err) {
      console.error('Submit error:', err);
      toast.error((err as Error).message || 'Kunne ikke sende forslag. Prøv igen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant} size={triggerSize} className="gap-2">
          <ImagePlus className="w-4 h-4" />
          Foreslå nyt billede
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Foreslå nyt billede</DialogTitle>
          <DialogDescription>
            For <strong>{variantLabel}</strong>. En admin gennemgår dit forslag inden det vises.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Honeypot */}
          <input
            type="text"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            className="hidden"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden
          />

          {/* Upload area */}
          <div className="space-y-2">
            <Label>Billede</Label>
            {imageUrl ? (
              <div className="relative">
                <img
                  src={imageUrl}
                  alt="Forhåndsvisning"
                  className="w-full max-h-56 object-contain rounded-md border bg-muted"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={() => setImageUrl('')}
                  className="absolute top-2 right-2 h-7 w-7"
                  title="Fjern billede"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex-1 gap-2"
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    Vælg fil
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePaste}
                    disabled={uploading}
                    title="Indsæt fra udklipsholder"
                    className="gap-2"
                  >
                    <ClipboardPaste className="w-4 h-4" />
                    Indsæt
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                    e.target.value = '';
                  }}
                />
                <div className="text-xs text-muted-foreground text-center">eller</div>
                <Input
                  type="url"
                  placeholder="Indsæt billede-URL (https://...)"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="source-url">Kilde (valgfri)</Label>
            <Input
              id="source-url"
              type="url"
              placeholder="Hvor stammer billedet fra? F.eks. pressefoto-link"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Noter (valgfri)</Label>
            <Textarea
              id="notes"
              placeholder="F.eks. ‘officielt pressefoto’ eller specifik farve/variant"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {!user && (
            <>
              <div className="space-y-2">
                <Label htmlFor="email">Din e-mail *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={submitterEmail}
                  onChange={(e) => setSubmitterEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Dit navn (valgfri)</Label>
                <Input
                  id="name"
                  value={submitterName}
                  onChange={(e) => setSubmitterName(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Annullér
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || uploading}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send forslag
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
